/**
 * Canonical home for the curve math — the charts' monotone cubic and the
 * pathway's Catmull-Rom spline — promoted here (out of
 * `@schmancy/primitives`'s `chart/curves.ts`) so a consumer BELOW the chart
 * module in the dependency graph can reach it too. `@schmancy/primitives`
 * depends on `@schmancy/directives` (chart needs directive-driven physics
 * elsewhere in that package), so `@schmancy/directives` importing from
 * `@schmancy/primitives` would be circular; `@schmancy/utils` sits below
 * both and is already a dependency of each. `chart/curves.ts` re-exports
 * these for its existing consumers; `pathway.ts` (directives) imports them
 * directly.
 */

/** A 2D point in an arbitrary coordinate space — chart-space, screen-space, or DOM-relative. */
export interface CurvePoint {
	x: number
	y: number
}

/**
 * Catmull-Rom spline interpolation for smooth curves. Returns the
 * interpolated point between p1 and p2 at parameter t (0 to 1), shaped by
 * the neighboring control points p0 and p3.
 */
export function catmullRomSpline(
	p0: CurvePoint,
	p1: CurvePoint,
	p2: CurvePoint,
	p3: CurvePoint,
	t: number,
): CurvePoint {
	const t2 = t * t
	const t3 = t2 * t

	const x =
		0.5 *
		(2 * p1.x +
			(-p0.x + p2.x) * t +
			(2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
			(-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3)

	const y =
		0.5 *
		(2 * p1.y +
			(-p0.y + p2.y) * t +
			(2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
			(-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3)

	return { x, y }
}

/** Cubic ease-out animation function. */
export function easeOutCubic(t: number): number {
	return 1 - Math.pow(1 - t, 3)
}

/** Straight line between consecutive points. */
export function linearPath(points: CurvePoint[]): CurvePoint[] {
	return points
}

/**
 * Step-after — each point's value holds flat until the next point's x is
 * reached, then risers vertically to the new value. Matches a running
 * total or closing-balance series, whose true value is constant between
 * updates and changes only at the update itself.
 */
export function stepPath(points: CurvePoint[]): CurvePoint[] {
	if (points.length === 0) return []
	const stepped: CurvePoint[] = [points[0]]
	for (let i = 1; i < points.length; i++) {
		stepped.push({ x: points[i].x, y: points[i - 1].y })
		stepped.push(points[i])
	}
	return stepped
}

/**
 * Monotone cubic (Fritsch–Carlson, d3's `curveMonotoneX`) smoothed curve, 16
 * interpolated samples per point-to-point span. Each span is a cubic Hermite
 * whose tangents are limited so the curve never leaves the band its two end
 * points span: no dip under a zero day beside a busy one, no overshoot past a
 * peak. A uniform Catmull-Rom spline carried its neighbours' slope across the
 * span and swung through both. Assumes x ascends, as a chart's points do.
 */
export function smoothPath(points: CurvePoint[]): CurvePoint[] {
	if (points.length < 2) return points
	const n = points.length
	const h: number[] = []
	const secant: number[] = []
	for (let i = 0; i < n - 1; i++) {
		h[i] = points[i + 1].x - points[i].x
		secant[i] = h[i] === 0 ? 0 : (points[i + 1].y - points[i].y) / h[i]
	}
	const tangent: number[] = Array.from({ length: n }, () => 0)
	for (let i = 1; i < n - 1; i++) {
		const s0 = secant[i - 1]
		const s1 = secant[i]
		if (s0 * s1 <= 0) {
			tangent[i] = 0
			continue
		}
		const weighted = (s0 * h[i] + s1 * h[i - 1]) / (h[i - 1] + h[i])
		tangent[i] = Math.sign(s0) * Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(weighted)) * 2
	}
	// End tangents: the one-sided three-point estimate, held to the band too.
	const endTangent = (s: number, inner: number) => {
		const t = (3 * s - inner) / 2
		return t * s <= 0 ? 0 : Math.sign(s) * Math.min(Math.abs(t), 3 * Math.abs(s))
	}
	tangent[0] = n === 2 ? secant[0] : endTangent(secant[0], tangent[1])
	tangent[n - 1] = n === 2 ? secant[n - 2] : endTangent(secant[n - 2], tangent[n - 2])

	const segments = 16
	const smoothed: CurvePoint[] = [points[0]]
	for (let i = 0; i < n - 1; i++) {
		const p1 = points[i]
		const p2 = points[i + 1]
		for (let j = 1; j <= segments; j++) {
			const t = j / segments
			const t2 = t * t
			const t3 = t2 * t
			const h00 = 2 * t3 - 3 * t2 + 1
			const h10 = t3 - 2 * t2 + t
			const h01 = -2 * t3 + 3 * t2
			const h11 = t3 - t2
			smoothed.push({
				x: p1.x + t * h[i],
				y: h00 * p1.y + h10 * h[i] * tangent[i] + h01 * p2.y + h11 * h[i] * tangent[i + 1],
			})
		}
	}
	return smoothed
}
